import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Compass, FileText, Map as MapIcon, Settings, Users, User, WifiOff, FlaskConical, Check, LogOut, PanelLeftClose, PanelLeftOpen, SquarePen, History } from 'lucide-react';
import { queryClient, setDemo, useApp, type Scope } from '../data/AppProvider';
import { useGroups, useMe } from '../data/hooks';
import { ConversationNav } from './elio/ConversationNav';
import { AskElioButton, ElioDrawerProvider } from './elio/ElioDrawer';
import { Avatar, Sheet, cx } from './ui';

/** The conversation with Elio is home; everything else supports it. */
const NAV = [
  { to: '/explore', label: 'Explore', icon: Compass },
  { to: '/trips', label: 'My Trips', short: 'Trips', icon: MapIcon },
  { to: '/groups', label: 'Groups', icon: Users },
  { to: '/documents', label: 'Documents', short: 'Wallet', icon: FileText },
];

function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

function readPref(key: string, fallback: string) { try { return window.localStorage.getItem(key) ?? fallback; } catch { return fallback; } }
function writePref(key: string, v: string) { try { window.localStorage.setItem(key, v); } catch { /* ignore */ } }

/** Personal vs group view, offered inside the profile menu. */
function ContextChoices({ onDone }: { onDone: () => void }) {
  const { scope, setScope, announce } = useApp();
  const groups = useGroups();
  const active = (groups.data ?? []).filter((g) => g.myStatus === 'active');
  if (active.length === 0) return null;
  const choose = (s: Scope) => {
    setScope(s);
    announce(s.kind === 'personal' ? 'Showing your own trips and documents.' : `Showing ${s.name}'s shared trips.`);
    onDone();
  };
  return (
    <div className="border-t border-line py-1">
      <p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted">View as</p>
      <button role="menuitemradio" aria-checked={scope.kind === 'personal'} type="button" onClick={() => choose({ kind: 'personal' })} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm hover:bg-ivory">
        <User className="size-4" aria-hidden /><span className="flex-1">Personal</span>{scope.kind === 'personal' && <Check className="size-4 text-forest-700" aria-hidden />}
      </button>
      {active.map((g) => (
        <button key={g.id} role="menuitemradio" aria-checked={scope.kind === 'group' && scope.groupId === g.id} type="button" onClick={() => choose({ kind: 'group', groupId: g.id, name: g.name })} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm hover:bg-ivory">
          <Users className="size-4" aria-hidden /><span className="min-w-0 flex-1 truncate">{g.name}</span>{scope.kind === 'group' && scope.groupId === g.id && <Check className="size-4 text-forest-700" aria-hidden />}
        </button>
      ))}
    </div>
  );
}

function ProfileMenu({ compact, placement = 'down' }: { compact?: boolean; placement?: 'up' | 'down' }) {
  const me = useMe();
  const { data, session } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const name = me.data?.profile.display_name ?? '';
  const signOut = async () => {
    await data.signOut();
    queryClient.clear();
    navigate('/sign-in');
  };
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} aria-label="Profile and settings"
        className={cx('flex items-center gap-2.5 rounded-full text-left transition hover:bg-white', compact ? 'p-0.5' : 'w-full p-1.5 pr-3')}>
        <Avatar name={name || '?'} color={me.data?.profile.avatar_color} />
        {!compact && <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{name.replace(/\s*\(.*?\)/g, '') || 'You'}</span><span className="block truncate text-xs text-muted">{session?.email ?? ''}</span></span>}
      </button>
      {open && (
        <div role="menu" className={cx('absolute z-50 w-64 rounded-2xl border border-line bg-white p-1 shadow-[var(--shadow-lift)]', placement === 'up' ? 'bottom-full left-0 mb-2' : 'right-0 top-full mt-2')}>
          <NavLink role="menuitem" to="/settings" onClick={() => setOpen(false)} className="flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm hover:bg-ivory"><Settings className="size-4" aria-hidden />Settings</NavLink>
          <ContextChoices onDone={() => setOpen(false)} />
          {data.kind !== 'demo' && (
            <div className="border-t border-line pt-1">
              <button role="menuitem" type="button" onClick={() => void signOut()} className="flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm hover:bg-ivory"><LogOut className="size-4" aria-hidden />Sign out</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DemoBanner() {
  const { data } = useApp();
  if (data.kind !== 'demo') return null;
  return (
    <div className="flex items-center justify-center gap-2 bg-gold-soft px-4 py-1.5 text-center text-xs text-gold-ink" role="note">
      <FlaskConical className="size-3.5 shrink-0" aria-hidden />
      <span className="min-w-0"><strong className="whitespace-nowrap">Demo, fictional data.</strong><span className="hidden sm:inline"> Nothing here is real or saved.</span></span>
      <button type="button" className="shrink-0 whitespace-nowrap underline underline-offset-2" onClick={() => setDemo(false)}>Leave demo</button>
    </div>
  );
}

function Logo({ collapsed }: { collapsed?: boolean }) {
  return (
    <NavLink to="/" aria-label="Elio Travels — talk to Elio" className="flex items-center gap-3 rounded-2xl px-2 py-1">
      <img src="/icon.svg" alt="" className="size-10 rounded-xl shadow-sm" />
      {!collapsed && <span className="font-display text-[1.6rem] leading-none text-forest-800">Elio Travels</span>}
    </NavLink>
  );
}

export function AppShell() {
  return <ElioDrawerProvider><Shell /></ElioDrawerProvider>;
}

function Shell() {
  const online = useOnline();
  const { scope } = useApp();
  const loc = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => readPref('elio-sidebar', 'open') === 'closed');
  const [historyOpen, setHistoryOpen] = useState(false);
  const isHome = loc.pathname === '/' || loc.pathname.startsWith('/c/');
  useEffect(() => { if (!isHome) window.scrollTo(0, 0); }, [loc.pathname, isHome]);
  const toggle = () => { setCollapsed((c) => { writePref('elio-sidebar', c ? 'open' : 'closed'); return !c; }); };

  return (
    <div className="min-h-dvh">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-lg focus:bg-white focus:px-3 focus:py-2">Skip to content</a>
      <DemoBanner />
      {!online && (
        <div className="flex items-center justify-center gap-2 bg-ink px-4 py-1.5 text-xs text-white" role="status">
          <WifiOff className="size-3.5" aria-hidden /> You're offline. Showing what was last loaded; changes can't be saved until you reconnect.
        </div>
      )}
      <div className="flex">
        {/* Desktop sidebar: logo (home = conversation), new conversation, navigation, recent conversations, profile */}
        <nav aria-label="Primary" className={cx('sticky top-0 hidden h-dvh shrink-0 flex-col gap-1 border-r border-line/60 bg-ivory py-4 transition-[width] lg:flex', collapsed ? 'w-[76px] px-3' : 'w-72 px-3')}>
          <div className={cx('mb-3 flex items-center', collapsed ? 'flex-col gap-2' : 'justify-between')}>
            <Logo collapsed={collapsed} />
            <button type="button" onClick={toggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="inline-flex size-10 items-center justify-center rounded-xl text-muted hover:bg-white hover:text-ink">
              {collapsed ? <PanelLeftOpen className="size-5" /> : <PanelLeftClose className="size-5" />}
            </button>
          </div>
          <button type="button" onClick={() => navigate('/', { state: { fresh: Date.now() } })} title="New conversation"
            className={cx('mb-2 inline-flex min-h-11 items-center gap-2 rounded-full bg-forest-700 text-sm font-semibold text-white shadow-sm hover:bg-forest-800', collapsed ? 'justify-center px-0' : 'px-4')}>
            <SquarePen className="size-4" aria-hidden />{!collapsed && 'New conversation'}
          </button>
          {NAV.map((n) => <SideLink key={n.to} {...n} collapsed={collapsed} />)}
          <div className="my-3 border-t border-line/70" />
          {!collapsed ? <ConversationNav /> : <div className="flex-1" />}
          <div className="mt-2 border-t border-line/70 pt-2"><ProfileMenu compact={collapsed} placement="up" /></div>
        </nav>

        <div className="min-w-0 flex-1">
          {/* Mobile top bar */}
          <header className="safe-top sticky top-0 z-30 flex items-center gap-2 border-b border-line bg-ivory/95 px-3 py-2 backdrop-blur lg:hidden">
            {isHome
              ? <button type="button" onClick={() => setHistoryOpen(true)} aria-label="Recent conversations" className="inline-flex size-10 items-center justify-center rounded-full text-muted hover:bg-white"><History className="size-5" /></button>
              : <NavLink to="/" aria-label="Talk to Elio" className="shrink-0"><img src="/icon.svg" alt="" className="size-9 rounded-xl" /></NavLink>}
            <span className="min-w-0 flex-1 truncate text-center font-display text-lg">{isHome ? 'Elio' : ''}</span>
            {isHome && <button type="button" onClick={() => navigate('/', { state: { fresh: Date.now() } })} aria-label="New conversation" className="inline-flex size-10 items-center justify-center rounded-full text-muted hover:bg-white"><SquarePen className="size-5" /></button>}
            <ProfileMenu compact />
          </header>
          {scope.kind === 'group' && !isHome && (
            <p className="border-b border-gold/30 bg-gold-soft/60 px-4 py-1.5 text-xs text-gold-ink lg:px-8">
              Viewing <strong>{scope.name}</strong>: shared trips and group documents.
            </p>
          )}
          <main id="main" className={cx(isHome ? 'px-3 pb-20 pt-2 sm:px-6 lg:px-8 lg:pb-4 lg:pt-4' : 'px-4 pb-28 pt-5 sm:px-6 lg:px-10 lg:pb-12 lg:pt-8')}>
            <Outlet />
          </main>
        </div>
      </div>

      <AskElioButton />
      <Sheet open={historyOpen} onClose={() => setHistoryOpen(false)} title="Conversations">
        <button type="button" onClick={() => { setHistoryOpen(false); navigate('/', { state: { fresh: Date.now() } }); }} className="mb-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-forest-700 px-4 text-sm font-semibold text-white"><SquarePen className="size-4" aria-hidden />New conversation</button>
        <ConversationNav onNavigate={() => setHistoryOpen(false)} />
      </Sheet>

      {/* Mobile bottom navigation: Elio (home) + four sections */}
      <nav aria-label="Primary" className="safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white/95 backdrop-blur lg:hidden">
        <ul className="mx-auto grid max-w-lg grid-cols-5">
          <li>
            <NavLink to="/" className={() => cx('flex min-h-14 flex-col items-center justify-center gap-0.5 text-[0.6875rem] font-semibold', isHome ? 'text-forest-700' : 'text-muted')}>
              <span className={cx('flex h-7 w-12 items-center justify-center rounded-full', isHome && 'bg-forest-50')}><img src="/icon.svg" alt="" className="size-6 rounded-md" /></span>
              Elio
            </NavLink>
          </li>
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} className={({ isActive }) => cx('flex min-h-14 flex-col items-center justify-center gap-0.5 text-[0.6875rem] font-medium', isActive ? 'text-forest-700' : 'text-muted')}>
                {({ isActive }) => (
                  <>
                    <span className={cx('flex h-7 w-12 items-center justify-center rounded-full', isActive && 'bg-forest-50')}><n.icon className="size-5" aria-hidden /></span>
                    <span className="max-w-full truncate whitespace-nowrap px-0.5">{'short' in n ? n.short : n.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function SideLink({ to, label, icon: Icon, collapsed }: { to: string; label: string; icon: typeof Compass; collapsed?: boolean }) {
  return (
    <NavLink to={to} title={collapsed ? label : undefined} aria-label={collapsed ? label : undefined}
      className={({ isActive }) => cx('flex min-h-11 items-center gap-3 rounded-xl text-[0.9375rem] transition-colors', collapsed ? 'justify-center px-0' : 'px-3', isActive ? 'bg-white font-semibold text-forest-800 shadow-[var(--shadow-card)]' : 'text-ink/75 hover:bg-white/70 hover:text-ink')}>
      {({ isActive }) => (<><Icon className={cx('size-5 shrink-0', isActive && 'text-forest-700')} aria-hidden />{!collapsed && label}</>)}
    </NavLink>
  );
}

export function PageTitle({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-display text-2xl leading-tight sm:text-3xl break-anywhere">{title}</h1>
        {subtitle && <div className="mt-1 text-sm text-muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/** Kept for screens that still offer a context switch inline. */
export function ContextSwitcher() { return null; }
